import React from 'react';
import { Check, X } from 'lucide-react';
import { T05SpotlightBorder } from './T05SpotlightBorder';

export interface T05PricingFeature {
  text: string;
  included: boolean;
}

export interface T05PricingPlan {
  eyebrow: string;
  name: string;
  price: string;
  priceNote?: string;
  description: string;
  features: T05PricingFeature[];
  featured?: boolean;
  badge?: string;
  wide?: boolean;
  bg: string;
  ctaLabel: string;
  onCta?: () => void;
}

export interface T05PricingSectionProps {
  id?: string;
  kicker?: string;
  title: string;
  subtitle?: string;
  aside?: string;
  plans: T05PricingPlan[];
  footnote?: string;
  className?: string;
  headerStyle?: 'pricing' | 'section';
}

const PricingCard: React.FC<{ plan: T05PricingPlan }> = ({ plan }) => (
  <T05SpotlightBorder
    className={plan.wide ? 't05-pricing__card--wide' : undefined}
    size={460}
    intensity={plan.featured ? 0.52 : 0.42}
  >
    <article
      className={`t05-pricing__card${plan.featured ? ' t05-pricing__card--featured' : ''}`}
      style={{ backgroundColor: plan.bg }}
    >
      {plan.badge && <div className="t05-pricing__badge">{plan.badge}</div>}

      <p className="t05-pricing__eyebrow">{plan.eyebrow}</p>
      <div className="t05-pricing__divider" aria-hidden />

      <h3 className="t05-pricing__name">{plan.name}</h3>

      <div className="t05-pricing__price-row">
        <span className="t05-pricing__price">{plan.price}</span>
      </div>
      {plan.priceNote && <p className="t05-pricing__price-note">{plan.priceNote}</p>}

      <p className="t05-pricing__desc">{plan.description}</p>

      <div className="t05-pricing__cta">
        <button
          type="button"
          className={plan.featured ? 't05-btn t05-btn--primary' : 't05-btn t05-btn--ghost'}
          onClick={plan.onCta}
        >
          {plan.ctaLabel}
        </button>
      </div>

      <ul className="t05-pricing__features">
        {plan.features.map((feature, index) => (
          <li
            key={feature.text}
            className={`t05-pricing__feature${feature.included ? '' : ' t05-pricing__feature--excluded'}${
              index > 0 ? ' t05-pricing__feature--divided' : ''
            }`}
          >
            <span
              className={`t05-pricing__feature-icon${
                feature.included ? '' : ' t05-pricing__feature-icon--excluded'
              }`}
              aria-hidden
            >
              {feature.included ? <Check size={12} strokeWidth={2.5} /> : <X size={12} strokeWidth={2.5} />}
            </span>
            {feature.text}
          </li>
        ))}
      </ul>
    </article>
  </T05SpotlightBorder>
);

export const T05PricingSection: React.FC<T05PricingSectionProps> = ({
  id = 'pricing',
  kicker = 'Tarife',
  title,
  subtitle,
  aside,
  plans,
  footnote,
  className = '',
  headerStyle = 'pricing',
}) => (
  <section
    id={id}
    className={`t05-pricing t05-prod-section t05-container reveal-up${
      headerStyle === 'section' ? ' t05-pricing--section-header' : ''
    }${className ? ` ${className}` : ''}`}
    aria-labelledby={`${id}-heading`}
  >
    {headerStyle === 'section' ? (
      <div className="t05-section__header reveal-up">
        <p className="t05-kicker">{kicker}</p>
        <h2 id={`${id}-heading`} className="t05-title">
          {title}
        </h2>
        {subtitle && <p className="t05-subtitle">{subtitle}</p>}
        {aside && <p className="t05-prod-footnote" style={{ marginTop: '1rem' }}>{aside}</p>}
      </div>
    ) : (
      <div className="t05-pricing__header">
        <div className="t05-pricing__header-main">
          <span className="t05-pricing__pill">
            <span className="t05-pricing__pill-dot" aria-hidden />
            {kicker}
          </span>
          <h2 id={`${id}-heading`} className="t05-pricing__title">
            {title}
          </h2>
          {subtitle && <p className="t05-pricing__subtitle">{subtitle}</p>}
        </div>
        {aside && <p className="t05-pricing__aside">{aside}</p>}
      </div>
    )}

    <div className="t05-pricing__grid">
      {plans.map((plan) => (
        <PricingCard key={plan.name} plan={plan} />
      ))}
    </div>

    {footnote && <p className="t05-pricing__footnote">{footnote}</p>}
  </section>
);
